"use client";

import { useEffect, useState } from "react";
import { ArrowRight, Building2, ShieldCheck, User } from "lucide-react";
import type { Locale } from "@/lib/i18n";
import {
  capture,
  getAnalyticsConsent,
  setAnalyticsConsent,
  type AnalyticsConsent,
} from "@/lib/analytics";

const STORAGE_KEY = "samvolt:onboarding";

export type Persona = "private" | "business";

const COPY = {
  sv: {
    welcomeTitle: "Välkommen till",
    welcomeAccent: "Samvolt",
    consentIntro:
      "Vi använder nödvändiga cookies för att webbplatsen ska fungera. Med ditt samtycke använder vi också analysverktyget PostHog för att se hur webbplatsen används och förbättra den – det lagrar en identifierare på din enhet som följer med in i appen. Du kan ändra ditt val när som helst längst ner på sidan.",
    termsBefore: "Genom att fortsätta bekräftar du att du tagit del av vår ",
    privacyLabel: "integritetspolicy",
    termsMiddle: " och våra ",
    termsLabel: "användarvillkor",
    termsAfter: ".",
    onlyNecessary: "Endast nödvändiga",
    acceptAnalytics: "Godkänn analys",
    personaTitle: "Vad beskriver dig",
    personaAccent: "bäst",
    personaQuestionMark: "?",
    personaSubtitle: "Så kan vi visa det som är mest relevant för just dig.",
    privateTitle: "Privatperson",
    privateSubtitle: "Sol, batteri och laddning för hemmet",
    businessTitle: "Företag",
    businessSubtitle: "Energioptimering för större anläggningar",
  },
  en: {
    welcomeTitle: "Welcome to",
    welcomeAccent: "Samvolt",
    consentIntro:
      "We use necessary cookies to make the site work. With your consent we also use PostHog analytics to see how the site is used and improve it – it stores an identifier on your device that follows you into the app. You can change your choice at any time at the bottom of the page.",
    termsBefore: "By continuing, you confirm that you have read our ",
    privacyLabel: "privacy policy",
    termsMiddle: " and our ",
    termsLabel: "terms of use",
    termsAfter: ".",
    onlyNecessary: "Necessary only",
    acceptAnalytics: "Accept analytics",
    personaTitle: "What describes you",
    personaAccent: "best",
    personaQuestionMark: "?",
    personaSubtitle: "So we can show you what is most relevant to you.",
    privateTitle: "Homeowner",
    privateSubtitle: "Solar, battery and charging for your home",
    businessTitle: "Business",
    businessSubtitle: "Energy optimization for larger installations",
  },
  no: {
    welcomeTitle: "Velkommen til",
    welcomeAccent: "Samvolt",
    consentIntro:
      "Vi bruker nødvendige informasjonskapsler for at nettstedet skal fungere. Med ditt samtykke bruker vi også analyseverktøyet PostHog for å se hvordan nettstedet brukes og forbedre det – det lagrer en identifikator på enheten din som følger med inn i appen. Du kan endre valget ditt når som helst nederst på siden.",
    termsBefore: "Ved å fortsette bekrefter du at du har lest vår ",
    privacyLabel: "personvernerklæring",
    termsMiddle: " og våre ",
    termsLabel: "brukervilkår",
    termsAfter: ".",
    onlyNecessary: "Kun nødvendige",
    acceptAnalytics: "Godta analyse",
    personaTitle: "Hva beskriver deg",
    personaAccent: "best",
    personaQuestionMark: "?",
    personaSubtitle: "Slik kan vi vise det som er mest relevant for deg.",
    privateTitle: "Privatperson",
    privateSubtitle: "Sol, batteri og lading for hjemmet",
    businessTitle: "Bedrift",
    businessSubtitle: "Energioptimalisering for større anlegg",
  },
  da: {
    welcomeTitle: "Velkommen til",
    welcomeAccent: "Samvolt",
    consentIntro:
      "Vi bruger nødvendige cookies, for at hjemmesiden kan fungere. Med dit samtykke bruger vi også analyseværktøjet PostHog til at se, hvordan hjemmesiden bruges, og forbedre den – det gemmer en identifikator på din enhed, som følger med ind i appen. Du kan ændre dit valg når som helst nederst på siden.",
    termsBefore: "Ved at fortsætte bekræfter du, at du har læst vores ",
    privacyLabel: "privatlivspolitik",
    termsMiddle: " og vores ",
    termsLabel: "brugervilkår",
    termsAfter: ".",
    onlyNecessary: "Kun nødvendige",
    acceptAnalytics: "Accepter analyse",
    personaTitle: "Hvad beskriver dig",
    personaAccent: "bedst",
    personaQuestionMark: "?",
    personaSubtitle: "Så kan vi vise det, der er mest relevant for dig.",
    privateTitle: "Privatperson",
    privateSubtitle: "Sol, batteri og opladning til hjemmet",
    businessTitle: "Virksomhed",
    businessSubtitle: "Energioptimering til større anlæg",
  },
  fi: {
    welcomeTitle: "Tervetuloa",
    welcomeAccent: "Samvolt",
    consentIntro:
      "Käytämme välttämättömiä evästeitä, jotta sivusto toimii. Suostumuksellasi käytämme myös PostHog-analytiikkaa nähdäksemme, miten sivustoa käytetään, ja kehittääksemme sitä – se tallentaa laitteellesi tunnisteen, joka seuraa mukana sovellukseen. Voit muuttaa valintaasi milloin tahansa sivun alalaidasta.",
    termsBefore: "Jatkamalla vahvistat lukeneesi ",
    privacyLabel: "tietosuojaselosteemme",
    termsMiddle: " ja ",
    termsLabel: "käyttöehtomme",
    termsAfter: ".",
    onlyNecessary: "Vain välttämättömät",
    acceptAnalytics: "Hyväksy analytiikka",
    personaTitle: "Mikä kuvaa sinua",
    personaAccent: "parhaiten",
    personaQuestionMark: "?",
    personaSubtitle: "Näin voimme näyttää sinulle olennaisimman sisällön.",
    privateTitle: "Yksityishenkilö",
    privateSubtitle: "Aurinko, akku ja lataus kotiin",
    businessTitle: "Yritys",
    businessSubtitle: "Energian optimointi suuremmille laitoksille",
  },
  de: {
    welcomeTitle: "Willkommen bei",
    welcomeAccent: "Samvolt",
    consentIntro:
      "Wir verwenden technisch notwendige Cookies, damit die Website funktioniert. Mit Ihrer Einwilligung nutzen wir zusätzlich das Analysewerkzeug PostHog, um zu sehen, wie die Website genutzt wird, und sie zu verbessern – dabei wird eine Kennung auf Ihrem Gerät gespeichert, die auch in der App weiterverwendet wird. Sie können Ihre Wahl jederzeit am Seitenende ändern.",
    termsBefore: "Wenn Sie fortfahren, bestätigen Sie, dass Sie unsere ",
    privacyLabel: "Datenschutzerklärung",
    termsMiddle: " und unsere ",
    termsLabel: "Nutzungsbedingungen",
    termsAfter: " gelesen haben.",
    onlyNecessary: "Nur notwendige",
    acceptAnalytics: "Analyse akzeptieren",
    personaTitle: "Was beschreibt Sie",
    personaAccent: "am besten",
    personaQuestionMark: "?",
    personaSubtitle: "So können wir Ihnen die relevantesten Inhalte zeigen.",
    privateTitle: "Privatperson",
    privateSubtitle: "Solar, Batterie und Laden für zu Hause",
    businessTitle: "Unternehmen",
    businessSubtitle: "Energieoptimierung für größere Anlagen",
  },
  cs: {
    welcomeTitle: "Vítejte v",
    welcomeAccent: "Samvolt",
    consentIntro:
      "Používáme nezbytné soubory cookie, aby web fungoval. S vaším souhlasem používáme také analytický nástroj PostHog, abychom viděli, jak se web používá, a mohli jej zlepšovat – ukládá do vašeho zařízení identifikátor, který vás provází i do aplikace. Svou volbu můžete kdykoli změnit v patičce stránky.",
    termsBefore: "Pokračováním potvrzujete, že jste si přečetli naše ",
    privacyLabel: "zásady ochrany osobních údajů",
    termsMiddle: " a naše ",
    termsLabel: "podmínky použití",
    termsAfter: ".",
    onlyNecessary: "Jen nezbytné",
    acceptAnalytics: "Přijmout analytiku",
    personaTitle: "Co vás nejlépe",
    personaAccent: "vystihuje",
    personaQuestionMark: "?",
    personaSubtitle: "Abychom vám mohli ukázat to nejrelevantnější.",
    privateTitle: "Domácnost",
    privateSubtitle: "Solár, baterie a nabíjení pro domov",
    businessTitle: "Firma",
    businessSubtitle: "Optimalizace energie pro větší instalace",
  },
  zh: {
    welcomeTitle: "欢迎使用",
    welcomeAccent: "Samvolt",
    consentIntro:
      "我们使用必要的 Cookie 以保证网站正常运行。在您同意的情况下，我们还会使用 PostHog 分析工具来了解网站的使用情况并加以改进——它会在您的设备上存储一个标识符，并会延续到应用中。您可以随时在页面底部更改您的选择。",
    termsBefore: "继续即表示您确认已阅读我们的",
    privacyLabel: "隐私政策",
    termsMiddle: "和",
    termsLabel: "使用条款",
    termsAfter: "。",
    onlyNecessary: "仅必要",
    acceptAnalytics: "接受分析",
    personaTitle: "哪一项最能",
    personaAccent: "描述您",
    personaQuestionMark: "？",
    personaSubtitle: "以便我们为您展示最相关的内容。",
    privateTitle: "个人",
    privateSubtitle: "为家庭提供太阳能、电池和充电",
    businessTitle: "企业",
    businessSubtitle: "为大型设施提供能源优化",
  },
} satisfies Record<Locale, Record<string, string>>;

function storedPersona(): Persona | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return parsed?.persona === "private" || parsed?.persona === "business"
      ? parsed.persona
      : null;
  } catch {
    return null;
  }
}

/**
 * First-visit onboarding shown once per browser:
 *   1. Analytics consent (accept / necessary only) — a real choice, because
 *      PostHog stays unloaded until it is answered.
 *   2. Pick persona (Privatperson / Företag)
 * Step 2 is skipped when a persona is already stored, so reopening this from
 * the footer to change the cookie choice doesn't re-ask everything.
 */
export function WelcomeDialog({
  onComplete,
  locale = "sv",
}: {
  onComplete?: (persona: Persona) => void;
  locale?: Locale;
}) {
  const t = COPY[locale];
  // Start closed + unmounted-visible so SSR and first client render match,
  // then decide on the client (localStorage isn't available on the server).
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);

  useEffect(() => {
    setReady(true);
    if (getAnalyticsConsent() === null) setOpen(true);
  }, []);

  // Lock background scroll while the dialog is up.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const answerConsent = (choice: AnalyticsConsent) => {
    setAnalyticsConsent(choice);
    capture("consent_answered", { analytics: choice });
    if (storedPersona()) {
      setOpen(false);
      return;
    }
    setStep(2);
  };

  const finish = (persona: Persona) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ persona, ts: Date.now() }),
      );
    } catch {
      /* storage unavailable – just close for this session */
    }
    capture("persona_selected", { persona });
    onComplete?.(persona);
    setOpen(false);
  };

  if (!ready || !open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center">
      {/* Backdrop – intentionally not click-to-dismiss so a choice is made */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-title"
        className="animate-fade-in-up relative z-10 w-full max-w-md overflow-hidden rounded-t-[28px] border border-white/10 bg-neutral-950/90 p-7 pb-[max(env(safe-area-inset-bottom),1.75rem)] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] backdrop-blur-2xl sm:rounded-[28px] sm:pb-7"
      >
        {/* Step indicator */}
        <div className="mb-6 flex items-center gap-1.5">
          <span
            className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
              step >= 1 ? "bg-white" : "bg-white/15"
            }`}
          />
          <span
            className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
              step >= 2 ? "bg-white" : "bg-white/15"
            }`}
          />
        </div>

        {step === 1 ? (
          <div>
            <span className="flex h-11 w-11 items-center justify-center rounded-[12px] bg-white/10 text-white">
              <ShieldCheck size={20} strokeWidth={1.75} />
            </span>
            <h2
              id="welcome-title"
              className="mt-5 font-sans text-[1.5rem] font-normal leading-[1.1] tracking-[-0.02em] text-white"
            >
              {t.welcomeTitle}{" "}
              <span className="font-newsreader font-normal italic text-white/70">
                {t.welcomeAccent}
              </span>
            </h2>
            <p className="mt-3 text-[0.9375rem] leading-relaxed text-white/55">
              {t.consentIntro}
            </p>
            <p className="mt-3 text-[0.9375rem] leading-relaxed text-white/55">
              {t.termsBefore}
              {/* Legal documents exist in Swedish only, so every locale links there. */}
              <a
                href="/integritetspolicy"
                className="text-white/80 underline underline-offset-2 hover:text-white"
              >
                {t.privacyLabel}
              </a>
              {t.termsMiddle}
              <a
                href="/anvandarvillkor"
                className="text-white/80 underline underline-offset-2 hover:text-white"
              >
                {t.termsLabel}
              </a>
              {t.termsAfter}
            </p>

            {/* Both options are the same size and sit side by side: refusing
                analytics has to be exactly as easy as accepting them. */}
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => answerConsent("denied")}
                className="flex flex-1 items-center justify-center rounded-[14px] border border-white/25 bg-white/10 px-5 py-3.5 text-[1rem] font-semibold text-white transition-colors hover:bg-white/20 active:bg-white/25"
              >
                {t.onlyNecessary}
              </button>
              <button
                type="button"
                onClick={() => answerConsent("granted")}
                className="flex flex-1 items-center justify-center rounded-[14px] border border-white bg-white px-5 py-3.5 text-[1rem] font-semibold text-black transition-colors hover:bg-white/90 active:bg-white/80"
              >
                {t.acceptAnalytics}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <h2
              id="welcome-title"
              className="font-sans text-[1.5rem] font-normal leading-[1.1] tracking-[-0.02em] text-white"
            >
              {t.personaTitle}{" "}
              <span className="font-newsreader font-normal italic text-white/70">
                {t.personaAccent}
              </span>
              {t.personaQuestionMark}
            </h2>
            <p className="mt-3 text-[0.9375rem] leading-relaxed text-white/55">
              {t.personaSubtitle}
            </p>

            <div className="mt-6 flex flex-col gap-3">
              <PersonaButton
                icon={<User size={20} strokeWidth={1.75} />}
                title={t.privateTitle}
                subtitle={t.privateSubtitle}
                onClick={() => finish("private")}
              />
              <PersonaButton
                icon={<Building2 size={20} strokeWidth={1.75} />}
                title={t.businessTitle}
                subtitle={t.businessSubtitle}
                onClick={() => finish("business")}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PersonaButton({
  icon,
  title,
  subtitle,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex items-center gap-4 rounded-[16px] border border-white/10 bg-white/[0.06] p-4 text-left transition-colors hover:border-white/25 hover:bg-white/10 active:bg-white/15"
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-white/10 text-white">
        {icon}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="text-[1.0625rem] font-medium text-white">{title}</span>
        <span className="truncate text-[0.8125rem] text-white/50">{subtitle}</span>
      </span>
      <ArrowRight
        size={18}
        strokeWidth={2}
        className="ml-auto shrink-0 text-white/30 transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-white/70"
      />
    </button>
  );
}
